import {apiLogger} from '@/features/core/api/logger';

/**
 * Type for API error data that can be extracted from responses
 */
export type ApiErrorData = Record<string, unknown> | string | null;

/**
 * Custom API error class with enhanced information about network and server errors
 */
export class ApiError extends Error {
  status: number;
  statusText: string;
  data: ApiErrorData;
  isNetworkError: boolean;

  constructor({
    message,
    status,
    statusText,
    data,
    isNetworkError = false,
  }: {
    message: string;
    status?: number;
    statusText?: string;
    data?: ApiErrorData;
    isNetworkError?: boolean;
  }) {
    super(message);
    this.name = 'ApiError';
    this.status = status ?? 0;
    this.statusText = statusText ?? '';
    this.data = data ?? null;
    this.isNetworkError = isNetworkError;

    // Log the error for debugging
    apiLogger.error('API Error:', {
      message,
      status,
      statusText,
      isNetworkError,
      data: data ? JSON.stringify(data).slice(0, 200) : undefined,
    });
  }

  /**
   * Create an ApiError from a Response object
   */
  static fromResponse(response: Response, data?: ApiErrorData): ApiError {
    return new ApiError({
      message: `API error: ${response.status} ${response.statusText}`,
      status: response.status,
      statusText: response.statusText,
      data,
    });
  }

  /**
   * Create an ApiError for network-related errors
   */
  static networkError(error: Error): ApiError {
    return new ApiError({
      message: `Network error: ${error.message}`,
      isNetworkError: true,
    });
  }

  /**
   * Check if this error is a timeout error (504 Gateway Timeout or network timeout)
   */
  isTimeout(): boolean {
    return (
      this.status === 504 || (this.isNetworkError && this.message.toLowerCase().includes('timeout'))
    );
  }

  /**
   * Get a user-friendly message for the error
   */
  getUserFriendlyMessage(): string {
    if (this.isTimeout()) {
      return 'The server took too long to respond. Are you online?';
    }

    if (this.isNetworkError) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }

    if (this.status >= 500) {
      return `Server error (${this.status}): The server encountered an error. Please try again later.`;
    }

    if (this.status === 401 || this.status === 403) {
      return "Authentication error: You don't have permission to access this resource.";
    }

    if (this.status === 404) {
      return 'The requested resource was not found.';
    }

    return this.message;
  }

  /**
   * Override toString to provide more detailed error information
   */
  toString(): string {
    return `ApiError: ${this.message} ${this.isNetworkError ? '(Network Error)' : `(Status: ${this.status})`}`;
  }
}
